import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import UserEndorsement from "../admin/UserEndorsement";
import { FiBell, FiX } from "react-icons/fi";
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { HiOutlineCog, HiOutlineHome } from 'react-icons/hi';

export default function AdminHeader() {
  const [unseenOrders, setUnseenOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    async function fetchOrderData() {
      try {
        const adminToken = Cookies.get('adminKey');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URI}admin/unseenOrders`, {
          method: 'GET',
          headers: {
            "Admin-Token": adminToken
          }
        });
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        const data = await response.json();
        setUnseenOrders(data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    }
    fetchOrderData();
    
    // Refresh notifications every minute
    const intervalId = setInterval(fetchOrderData, 60000);
    return () => clearInterval(intervalId);
  }, []);

  // Animation variants
  const badgeVariants = {
    initial: { scale: 0 },
    animate: { 
      scale: 1,
      transition: { 
        type: "spring", 
        stiffness: 500, 
        damping: 17 
      }
    }
  };

  const dropdownVariants = {
    hidden: { 
      opacity: 0,
      y: -10,
      scale: 0.95
    },
    visible: { 
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.2,
        staggerChildren: 0.05,
        when: "beforeChildren"
      }
    },
    exit: { 
      opacity: 0,
      y: -10,
      scale: 0.95,
      transition: { duration: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="w-full bg-gradient-to-r from-[#121212] to-[#1a1a1a] shadow-md flex justify-between items-center px-6 py-3 text-white z-50"
    >
      <motion.div 
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.2, duration: 0.4 }}
        className="flex items-center space-x-6"
      >
        <h1 className="text-xl font-medium">Pannello Amministrazione</h1>
        <div className="hidden md:flex items-center space-x-4 text-sm text-gray-300">
          <Link to="/" className="flex items-center hover:text-white transition-colors">
            <HiOutlineHome className="mr-1" />
            <span>Home</span>
          </Link>
          <Link to="/admin" className="flex items-center hover:text-white transition-colors">
            <HiOutlineCog className="mr-1" />
            <span>Dashboard</span>
          </Link>
        </div>
      </motion.div>

      <motion.div 
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.3, duration: 0.4 }}
        className="flex items-center space-x-4"
      >
        <div className="relative">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="relative flex items-center justify-center h-10 w-10 rounded-full bg-[#303030] hover:bg-[#404040] transition-colors"
            onClick={() => setShowDropdown(!showDropdown)}
            aria-label="Notifications"
          >
            <FiBell size={20} />
            <AnimatePresence>
              {unseenOrders.length > 0 && (
                <motion.div
                  key="badge"
                  variants={badgeVariants}
                  initial="initial"
                  animate="animate"
                  exit="initial"
                  className="absolute -top-1 -right-1 bg-red-500 w-5 h-5 rounded-full flex items-center justify-center text-white text-xs font-medium shadow-sm"
                >
                  {unseenOrders.length}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.button>

          <AnimatePresence>
            {showDropdown && (
              <motion.div
                variants={dropdownVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="absolute right-0 mt-2 py-2 w-80 bg-[#1E1E1E] rounded-lg shadow-lg max-h-[400px] overflow-y-auto z-50 border border-gray-800"
              >
                <div className="flex items-center justify-between px-4 pb-2 border-b border-gray-800">
                  <h3 className="font-medium text-sm">Notifiche</h3>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setShowDropdown(false)}
                    className="text-gray-400 hover:text-white"
                  >
                    <FiX size={18} />
                  </motion.button>
                </div>
                
                <div className="pt-2">
                  {loading ? (
                    <div className="flex justify-center py-6">
                      <div className="w-6 h-6 border-2 border-gray-600 border-t-white rounded-full animate-spin"></div>
                    </div>
                  ) : unseenOrders.length === 0 ? (
                    <motion.div 
                      variants={itemVariants}
                      className="text-gray-400 text-center py-6"
                    >
                      Nessun nuovo ordine
                    </motion.div>
                  ) : (
                    <div className="space-y-1">
                      {unseenOrders.map((order, index) => (
                        <motion.div 
                          key={order.id}
                          variants={itemVariants}
                          className="hover:bg-[#2A2A2A] transition-colors"
                        >
                          <Link
                            to={`/admin/orders/${order.id}`}
                            onClick={() => setShowDropdown(false)}
                            className="block px-4 py-3"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                                <span className="text-sm font-medium">Ordine #{order.id}</span>
                              </div>
                              <span className={`text-xs px-2 py-1 rounded ${
                                order.status === "pending" ? "bg-yellow-500/20 text-yellow-300" : 
                                order.status === "paid" ? "bg-green-500/20 text-green-300" : 
                                "bg-blue-500/20 text-blue-300"
                              }`}>
                                {order.status === "pending" ? "In Attesa" : 
                                 order.status === "paid" ? "Pagato" : order.status}
                              </span>
                            </div>
                            <p className="text-xs text-gray-400 mt-1">
                              {new Date(order.orderCreationTime).toLocaleString()}
                            </p>
                          </Link>
                          {index < unseenOrders.length - 1 && (
                            <div className="h-px bg-gray-800 mx-4"></div>
                          )}
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
                
                {unseenOrders.length > 0 && (
                  <div className="px-4 pt-2 mt-2 border-t border-gray-800">
                    <Link
                      to="/admin?component=orders"
                      onClick={() => setShowDropdown(false)}
                      className="block text-center py-2 text-sm text-blue-400 hover:text-blue-300 transition-colors"
                    >
                      Visualizza tutti gli ordini
                    </Link>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        
        <UserEndorsement />
      </motion.div>
    </motion.div>
  );
}